import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {CameraImageDraw} from 'src/common/components/camera-image-draw/CameraImageDraw'
import {transformCoordinatesIntoPoints, transformPointsIntoCoordinates} from 'src/common/generics/components/image-draw/ImageDrawFunctions'
import {Pos} from 'src/common/types/generics/ImageDetections'
import {VerifyUserAndCompanyPermission} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {Input} from 'src/components/input/Input'
import useImage from 'src/hooks/useImage'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'
import {BarrierType} from 'src/services/api'
import api from 'src/services/api/api'
import {BarrierTypeResponse, CreateBarrier} from 'src/services/api/endpoints/BarriersEndpoint'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'

import {
  RegisterBarrierImageDrawContainer,
  RegisterBarrierModalBody,
  RegisterBarrierModalContent,
  RegisterBarrierModalFooter,
} from './CameraModalRegisterBarrierBase'

type CameraModalRegisterBarrierProps = {
  open: boolean
  setOpen: (vari: boolean) => void
  editingArea?: BarrierTypeResponse | null
  setEditingArea: (edit: BarrierTypeResponse | null) => void
  snapshot: string
  refetchBarriers: () => void
  onBarrierSaved?: (barrier: BarrierTypeResponse) => void | Promise<void>
}

const BarriersTypesToList = [{id: 'interest-area', label: 'Área de interesse'}]

const BarriersTypesWithLineToList = [
  {id: 'interest-area', label: 'Área de interesse'},
  {id: 'line-crossing', label: 'Cruzamento de linha'},
]

export function CameraModalRegisterBarrier({
  open,
  setOpen,
  editingArea,
  setEditingArea,
  snapshot,
  refetchBarriers,
  onBarrierSaved,
}: CameraModalRegisterBarrierProps) {
  const [barrierName, setBarrierName] = useState<string>('')
  const [barrierType, setBarrierType] = useState<DropdownItem>(
    () => BarriersTypesWithLineToList.find((it) => it.id === editingArea?.type) ?? BarriersTypesToList[0],
  )

  const [saveString, setSaveString] = useState<string>()
  const [points, setPoints] = useState<Pos[]>([])

  const {_dimImage, camera} = useCameraConfigContext()
  const {enqueueSnackbar} = useSnackbar()
  const {width: naturalWidth, height: naturalHeight, isLoaded} = useImage(`data:image/jpeg;base64,${snapshot}`)

  useEffect(() => {
    if (!isLoaded || naturalWidth === 0 || naturalHeight === 0) return

    if (editingArea) {
      setPoints(transformCoordinatesIntoPoints(editingArea.coordinates, {width: naturalWidth, height: naturalHeight}))
      setBarrierName(editingArea.name)
    }
  }, [isLoaded, naturalWidth, naturalHeight, editingArea])

  const validatePoints = (points: Pos[]) => {
    if (!barrierName || !barrierType.id) return false
    else if (barrierType.id === 'interest-area' && points.length <= 3) return false
    else if (barrierType.id === 'line-crossing' && (points.length <= 1 || points.length > 2)) return false
    return true
  }

  const resetForm = () => {
    setBarrierName('')
    setPoints([])
    setSaveString(undefined)
  }

  const closeModal = () => {
    setEditingArea(null)
    resetForm()
    setOpen(false)
  }

  const dismissModal = () => {
    resetForm()
    setOpen(false)
  }

  const finishSave = async (barrier: BarrierTypeResponse) => {
    await onBarrierSaved?.(barrier)
  }

  async function addBarrier(points: Pos[]) {
    if (!barrierName || !barrierType.id || !camera) return
    if (_dimImage.width === 0 || _dimImage.height === 0) return

    const coordinates = transformPointsIntoCoordinates(points, _dimImage)

    try {
      const createBarrier = new CreateBarrier(camera.id, coordinates, barrierName, barrierType.id as BarrierType)
      const response = await api.barriers.createBarrier(createBarrier)

      enqueueSnackbar('Barreira salva com sucesso', {
        variant: 'success',
      })
      await finishSave(response.data.data)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao salvar a barreira')
    }
  }

  async function editBarrier(points: Pos[]) {
    if (!editingArea) return
    if (_dimImage.width === 0 || _dimImage.height === 0) return

    try {
      const response = await api.barriers.updateBarrier({
        coordinates: transformPointsIntoCoordinates(points, _dimImage),
        name: barrierName,
        id: editingArea.id,
      })
      enqueueSnackbar('Barreira atualizada com sucesso', {
        variant: 'success',
      })
      await finishSave(response.data.data)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao atualizar barreira')
    }
  }

  const hasCreateLineCrossingPermission = VerifyUserAndCompanyPermission([camera?.companyId!], 'line-crossing:create')
  const cameraIsRtsp = camera && (camera.origin === 'RTSP' || camera.origin === 'RTMP')
  return (
    <Modal
      isOpen={open}
      closeModal={() => closeModal()}
      backgroundColor='white'
      width='min(800px, calc(100vw - 32px))'
      padding='0'>
      <RegisterBarrierModalContent>
        <Box padding='20px 20px 0'>
          <Typography variant='p' size='18px' weight={500} children='Criar nova barreira' />
          <BtnClose onClose={() => closeModal()} />
        </Box>

        <RegisterBarrierModalBody>
          <Input.Root
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='14px'
            onChange={(e) => setBarrierName(e.target.value)}
            label='Nome'
            value={barrierName}
            controlled
            min={4}
            max={100}
            maxLength={100}
          />
          <Box display='flex' height='80px'>
            <Dropdown
              labelFontSize='14px'
              borderColor='#D0D5DD'
              fontSize='14px'
              height='40px'
              label='Tipo da barreira'
              onChangeSelected={(e) => setBarrierType(e)}
              selected={barrierType}
              items={cameraIsRtsp && hasCreateLineCrossingPermission ? BarriersTypesWithLineToList : BarriersTypesToList}
              disabled={!!editingArea}
            />
          </Box>
          <Typography variant='p' size='14px' weight={500} color='rgb(54, 65, 82);'>
            Crie os pontos para formar uma {barrierType?.id === 'interest-area' ? 'área' : 'linha'}
          </Typography>
          <RegisterBarrierImageDrawContainer>
            <CameraImageDraw
              isKonvaImageDrawToolbar
              width={300}
              height={200}
              points={points}
              editable={true}
              maxAmountOfPoints={barrierType.id === 'line-crossing' ? 2 : undefined}
              src={`data:image/jpeg;base64,${snapshot}`}
              onSave={(points) => {
                if (!validatePoints(points)) {
                  enqueueSnackbar('A quantidade de pontos não corresponde com o tipo da barreira escolhida', {variant: 'info'})
                  setSaveString(undefined)
                  return
                }

                dismissModal()

                if (editingArea) void editBarrier(points)
                else void addBarrier(points)
              }}
              save={saveString}
            />
          </RegisterBarrierImageDrawContainer>
          {barrierType.id === 'line-crossing' && (
            <Box display='flex' rowGap='8px' direction='column'>
              <b>Legenda:</b>
              <Box display='flex' align='center' columnGap='10px' justifyContent='center'>
                <Box backgroundColor='rgba(0,0,255,0.25)' width='30px' height='20px' borderRadius='3px'></Box>
                <Typography variant='p' textAlign='justify'>
                  A área azul indica a entrada do cruzamento. Quando o objeto atravessar a linha em direção ao lado azul, o sistema entende que houve uma
                  detecção.
                </Typography>
              </Box>
              <Typography variant='p'>
                <b>Dica:</b> Arraste ou inverta os pontos até que a área azul indique a direção desejada.
              </Typography>
            </Box>
          )}
        </RegisterBarrierModalBody>

        <RegisterBarrierModalFooter>
          <Button fontSize='12px' height='40px' width='150px' variant='outlined' color='#8E8E8E' onClick={() => closeModal()}>
            Cancelar
          </Button>

          <Button
            fontSize='12px'
            disabledTextColor='#8E8E8E'
            height='40px'
            width='150px'
            onClick={() => setSaveString('save')}
            disabled={!barrierName || !barrierType.id}>
            Salvar
          </Button>
        </RegisterBarrierModalFooter>
      </RegisterBarrierModalContent>
    </Modal>
  )
}
